/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { TIMEFRAME_UNITS } from 'cio/utils/round-timeframe-units';
import timeframeUnits from 'cio/utils/timeframe-units';
import timeframePropertyComputed from 'cio/utils/timeframe-computed';
import { defineProperty, action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { TimeFrameUnitType } from 'cio/components/campaigns/trigger-setup/frequency-controls';

const SECONDS_IN_HOUR = 60 * 60;
export const DEFAULT_TIMEFRAME_UNITS = ['day', 'hour'];

interface TimeSelectorArgs {
  label: string;
  prefix?: string;
  seconds: number;
  disabled: boolean;
  minSeconds?: number;
  timeframeUnits?: TimeFrameUnitType[];
  size?: 'medium' | 'small';
  countInputClassNames: string;
  unitInputClassNames: string;
  onChange: (seconds: number) => unknown;
  hasReadOnlyPermission: boolean;
  max?: number;
}

const DEFAULT_SIZE = 'medium';
const DEFAULT_COUNT_INPUT_CLASS_NAMES = '';
const DEFAULT_UNIT_INPUT_CLASS_NAMES = '';

export default class TimeSelector extends Component<TimeSelectorArgs> {
  get sizeVariant() {
    return this.args.size ?? DEFAULT_SIZE;
  }

  get countInputClassNames() {
    return this.args.countInputClassNames ?? DEFAULT_COUNT_INPUT_CLASS_NAMES;
  }

  get unitInputClassNames() {
    return this.args.unitInputClassNames ?? DEFAULT_UNIT_INPUT_CLASS_NAMES;
  }

  get minSeconds() {
    return this.args.minSeconds ?? SECONDS_IN_HOUR;
  }

  get timeframeUnits() {
    return this.args.timeframeUnits ?? DEFAULT_TIMEFRAME_UNITS;
  }

  get availableUnits() {
    let units = this.timeframeUnits;
    return TIMEFRAME_UNITS.filter((u) => units.includes(u.value));
  }

  get showErrors() {
    return this.isTouched && this.errors.length > 0;
  }

  get max() {
    if (this.args.max != null) {
      let max = Number(this.args.max);
      if (Number.isInteger(max) && max > 0) {
        return max;
      }
    }
    return null;
  }

  get errors() {
    let errors = [];
    if (this.selectedFrequencyPeriodCount === 0) {
      errors.push('Please select a whole number greater than 0');
    }
    if (this.max != null && Number(this.selectedFrequencyPeriodCount) > this.max) {
      errors.push(`Max number allowed is ${this.max}`);
    }
    return errors;
  }

  @tracked frequencyPeriodUnitOptions = timeframeUnits({
    context: this,
    countProperty: 'selectedFrequencyPeriodCount',
    units: this.availableUnits,
  });

  @tracked isTouched = false;
  @tracked selectedFrequencyPeriodCount: number | null = null;
  @tracked selectedFrequencyPeriodUnit: string | null = null;

  selectedFrequencyPeriodSeconds = 0;

  constructor(owner: unknown, args: TimeSelectorArgs) {
    super(owner, args);

    // Using defineProperty instead of just defining a computed property,
    // because a different set of "units" can be passed in from the outside
    // (this.timeframeUnits, and we do the filtering above),
    // and "timeframePropertyComputed" doesn't accept a key reference (dependent
    // key) for the units, so we have to pass it in like this.
    defineProperty(
      this,
      'selectedFrequencyPeriodSeconds',
      timeframePropertyComputed({
        countProperty: 'selectedFrequencyPeriodCount',
        unitProperty: 'selectedFrequencyPeriodUnit',
        units: this.availableUnits,
      }),
    );
  }

  @action
  setInputValues() {
    let { minSeconds } = this;
    set(this, 'selectedFrequencyPeriodSeconds', this.args.seconds < minSeconds ? minSeconds : this.args.seconds);
  }

  @action
  onChange() {
    this.args.onChange(this.selectedFrequencyPeriodSeconds);
  }

  @action
  setFrequencyPeriodUnit(val: string) {
    this.selectedFrequencyPeriodUnit = val;
    this.onChange();
  }

  @action
  setFrequencyPeriodCount(val: number) {
    this.selectedFrequencyPeriodCount = val;

    // not all browsers are guaranteed to honor "min"/"step" attributes on <input type="number">
    // ensure we're always setting positive, round number value:
    val = Math.round(val);
    if (val < 1) {
      val = 0;
    }
    if (!this.isTouched) {
      this.isTouched = true;
    }

    this.selectedFrequencyPeriodCount = val;
    if (val !== 0) {
      this.onChange();
    }
  }
}
